import { ReactElement, ReactNode, useEffect } from 'react'
import produce from 'immer'
import { cloneDeep, reject, uniq } from 'lodash-es'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import type { CharacteristicMemberSupportReactions } from '@queries'

type ResultElement = 'slabPositions' | 'roofSlabPositions' | 'anchorIndicators'

type VisibilityElements = ResultElement

type SelectedCell = { field: string }

/**
 * Currently highlighted check in the Bemessung detail pane. A `support` is
 * keyed by position only (one support per position) and rendered via its
 * `A`-label in the scene. Any other check is additionally keyed by its check
 * type (multiple checks can share a position) and rendered with a marker at
 * its position.
 */
export type HighlightedCheck =
  | { kind: 'support'; elementGuid: string; relativePosition: string }
  | {
      kind: 'check'
      elementGuid: string
      relativePosition: string
      checkType: MemberCheckType
    }
  | null

export type CornerConnectionAdditionalLoadProposalStates =
  | { type: 'idle' }
  | ({ type: 'selected-anchor'; anchorGuid: string } & SelectedCell)
  | ({
      type: 'selected-anchor-and-wall'
      anchorGuid: string
      wallGuid: string
    } & SelectedCell)
  | ({
      type: 'proposal-created'
      anchorGuid: string
      wallGuid: string
      proposal: CornerConnectionProposal
    } & SelectedCell)
  | ({
      type: 'proposal-changed'
      anchorGuid: string
      wallGuid: string
      proposal: CornerConnectionProposal
    } & SelectedCell)

interface Props {
  children: ReactNode
}

interface ResultsInitialState {
  segmentForces: SegmentForces[]
  stiffeningSegmentChecks: StiffeningSegmentCheck[]
  anchorChecks: AnchorCheckOnSupport[] | null
  elementLoads: ElementLoad[] | null
  positionLoads: ElementLoad[] | null
  structuralChecks: CombinedPositionCheck[] | null
  standardRipChecks: StandardRipCheck[] | null
  designForces: CombinedDesignSupportForcesOnPosition[] | null
  anchorInterventionTableData: AnchorInterventionsTableRowData[] | null
  characteristicMemberSupportReactions: CharacteristicMemberSupportReactions[] | null

  wallRipsPositionGrouping: MemberPositionBundle[] | null
  wallLintelsPositionGrouping: MemberPositionBundle[] | null
  slabBeamsPositionGrouping: MemberPositionBundle[] | null
  roofSlabBeamsPositionGrouping: MemberPositionBundle[] | null
  beamsPositionGrouping: MemberPositionBundle[] | null
  purlinsPositionGrouping: MemberPositionBundle[] | null
  columnsPositionGrouping: MemberPositionBundle[] | null
  memberCheckSettings: SettingsOnMember[]
  memberCheckSettingsFromLastComputation: SettingsOnMember[]
  crossSectionsFromLastComputation: ElementCSAssignment[]
  supportConfigsForChecks: SupportConfigForChecks[]

  hiddenLoads: string[]

  resultElementVisibility: Record<VisibilityElements, boolean>

  cornerConnectionAdditionalLoadProposal: CornerConnectionAdditionalLoadProposalStates

  selectedProposalAnchor: string | null

  /** Unsaved position-grouping edits in the Results2 element list form. */
  elementListGroupingDirty: boolean

  /** Last blurred comment field values — reused as autocomplete suggestions in Results2. */
  commentSuggestion: string
  extendedCommentSuggestion: string
  /** Separate pool for the Anker list — anchor comments are unrelated to position-list texts. */
  anchorCommentSuggestion: string
  noteSuggestion: string

  highlightedCheck: HighlightedCheck
}

interface ResultsStoreType extends ResultsInitialState {
  clear: () => void
  clearForCheckReRun: () => void

  setSegmentForces: (segmentForces: SegmentForces[]) => void
  setStiffeningSegmentChecks: (stiffeningSegmentChecks: StiffeningSegmentCheck[]) => void
  setAnchorChecks: (anchorChecks: AnchorCheckOnSupport[]) => void
  setElementLoads: (elementLoads: ElementLoad[]) => void
  setPositionLoads: (positionLoads: ElementLoad[]) => void
  setStructuralChecks: (structuralChecks: CombinedPositionCheck[]) => void
  /**
   * Replaces the structural checks for `elementGuids`, leaving every other
   * element's checks untouched — for callers that recompute a subset of
   * elements and must not clobber the rest of the store's checks. `elementGuids`
   * is taken explicitly (rather than derived from `newChecks`) so an element
   * whose recompute legitimately yields zero checks still has its old, stale
   * checks cleared.
   */
  replaceStructuralChecksForElements: (
    elementGuids: string[],
    newChecks: CombinedPositionCheck[],
  ) => void
  setStandardRipChecks: (standardRipChecks: StandardRipCheck[]) => void
  setAnchorInterventionTableData: (
    anchorInterventionTableData: AnchorInterventionsTableRowData[],
  ) => void

  setWallRipsPositionGrouping: (wallRipsPositionGrouping: MemberPositionBundle[]) => void
  setWallLintelsPositionGrouping: (wallLintelsPositionGrouping: MemberPositionBundle[]) => void
  setSlabBeamsPositionGrouping: (slabBeamsPositionGrouping: MemberPositionBundle[]) => void
  setRoofSlabBeamsPositionGrouping: (roofSlabBeamsPositionGrouping: MemberPositionBundle[]) => void
  setBeamsPositionGrouping: (beamsPositionGrouping: MemberPositionBundle[]) => void
  setPurlinsPositionGrouping: (purlinsPositionGrouping: MemberPositionBundle[]) => void
  setColumnsPositionGrouping: (columnsPositionGrouping: MemberPositionBundle[]) => void

  setHiddenLoads: (loads: string[]) => void

  setMemberCheckSettings: (memberCheckSettings: SettingsOnMember[]) => void
  setSingleMemberCheckSetting: (settings: SettingsOnMember) => void
  setMemberCheckSettingsFromLastComputation: (memberCheckSettings: SettingsOnMember[]) => void
  setCrossSectionsFromLastComputation: (crossSectionAssignment: ElementCSAssignment[]) => void
  setSupportConfigsForChecks: (supportConfigsForChecks: SupportConfigForChecks[]) => void

  clearHiddenLoads: () => void

  setDesignForces: (designForces: CombinedDesignSupportForcesOnPosition[] | null) => void
  setCharacteristicMemberSupportReactions: (
    data: CharacteristicMemberSupportReactions[] | null,
  ) => void

  changeResultElementVisibility: (element: VisibilityElements, visibility: boolean) => void

  setCornerConnectionAdditionalLoadProposal: (
    cornerConnectionAdditionalLoadProposal: CornerConnectionAdditionalLoadProposalStates,
  ) => void

  setSelectedProposalAnchor: (selectedProposalAnchor: string | null) => void
  setElementListGroupingDirty: (elementListGroupingDirty: boolean) => void
  setCommentSuggestion: (commentSuggestion: string) => void
  setExtendedCommentSuggestion: (extendedCommentSuggestion: string) => void
  setAnchorCommentSuggestion: (anchorCommentSuggestion: string) => void
  setNoteSuggestion: (noteSuggestion: string) => void
  setHighlightedCheck: (highlightedCheck: HighlightedCheck) => void
}

const initialState: ResultsInitialState = {
  segmentForces: [],
  stiffeningSegmentChecks: [],
  anchorChecks: [],
  elementLoads: [],
  positionLoads: [],
  structuralChecks: [],
  standardRipChecks: [],
  designForces: null,
  anchorInterventionTableData: null,
  characteristicMemberSupportReactions: null,

  wallRipsPositionGrouping: null,
  wallLintelsPositionGrouping: null,
  slabBeamsPositionGrouping: null,
  roofSlabBeamsPositionGrouping: null,
  beamsPositionGrouping: null,
  columnsPositionGrouping: null,
  purlinsPositionGrouping: null,

  hiddenLoads: [],

  memberCheckSettings: [],
  memberCheckSettingsFromLastComputation: [],
  crossSectionsFromLastComputation: [],
  supportConfigsForChecks: [],

  resultElementVisibility: {
    roofSlabPositions: false,
    slabPositions: false,
    anchorIndicators: false,
  },

  cornerConnectionAdditionalLoadProposal: { type: 'idle' },

  selectedProposalAnchor: null,

  elementListGroupingDirty: false,

  commentSuggestion: '',
  extendedCommentSuggestion: '',
  anchorCommentSuggestion: '',
  noteSuggestion: '',

  highlightedCheck: null,
}

const createStore = () =>
  create<ResultsStoreType>(
    combine(cloneDeep(initialState), set => ({
      clear: () => set(cloneDeep(initialState)),
      clearForCheckReRun: () =>
        set(
          produce(state => {
            state.stiffeningSegmentChecks = []
            state.anchorChecks = []
            state.structuralChecks = []
            state.standardRipChecks = []
          }),
        ),

      setSegmentForces: (segmentForces: SegmentForces[]) => set({ segmentForces }),
      setStiffeningSegmentChecks: (stiffeningSegmentChecks: StiffeningSegmentCheck[]) =>
        set({ stiffeningSegmentChecks }),
      setAnchorChecks: (anchorChecks: AnchorCheckOnSupport[]) => set({ anchorChecks }),
      setElementLoads: (elementLoads: ElementLoad[]) => set({ elementLoads }),
      setPositionLoads: (positionLoads: ElementLoad[]) => set({ positionLoads }),
      setStructuralChecks: (structuralChecks: CombinedPositionCheck[]) => set({ structuralChecks }),
      replaceStructuralChecksForElements: (
        elementGuids: string[],
        newChecks: CombinedPositionCheck[],
      ) =>
        set(state => {
          const affectedGuids = uniq(elementGuids)
          return {
            structuralChecks: [
              ...reject(state.structuralChecks ?? [], check =>
                affectedGuids.includes(check.element_guid),
              ),
              ...newChecks,
            ],
          }
        }),
      setStandardRipChecks: (standardRipChecks: StandardRipCheck[]) => set({ standardRipChecks }),
      setAnchorInterventionTableData: (
        anchorInterventionTableData: AnchorInterventionsTableRowData[],
      ) => set({ anchorInterventionTableData }),

      setWallRipsPositionGrouping: (wallRipsPositionGrouping: MemberPositionBundle[]) =>
        set({ wallRipsPositionGrouping }),
      setWallLintelsPositionGrouping: (wallLintelsPositionGrouping: MemberPositionBundle[]) =>
        set({ wallLintelsPositionGrouping }),
      setSlabBeamsPositionGrouping: (slabBeamsPositionGrouping: MemberPositionBundle[]) =>
        set({ slabBeamsPositionGrouping }),

      setRoofSlabBeamsPositionGrouping: (roofSlabBeamsPositionGrouping: MemberPositionBundle[]) =>
        set({ roofSlabBeamsPositionGrouping }),

      setBeamsPositionGrouping: (beamsPositionGrouping: MemberPositionBundle[]) =>
        set({ beamsPositionGrouping }),
      setPurlinsPositionGrouping: (purlinsPositionGrouping: MemberPositionBundle[]) =>
        set({ purlinsPositionGrouping }),
      setColumnsPositionGrouping: (columnsPositionGrouping: MemberPositionBundle[]) =>
        set({ columnsPositionGrouping }),

      setHiddenLoads: (hiddenLoads: string[]) => set({ hiddenLoads }),

      clearHiddenLoads: () => set({ hiddenLoads: [] }),

      setMemberCheckSettings: (memberCheckSettings: SettingsOnMember[]) =>
        set({ memberCheckSettings }),

      setMemberCheckSettingsFromLastComputation: (memberCheckSettings: SettingsOnMember[]) =>
        set({ memberCheckSettingsFromLastComputation: memberCheckSettings }),

      setCrossSectionsFromLastComputation: (crossSections: ElementCSAssignment[]) =>
        set({ crossSectionsFromLastComputation: crossSections }),

      setSupportConfigsForChecks: (supportConfigsForChecks: SupportConfigForChecks[]) =>
        set({ supportConfigsForChecks }),

      setSingleMemberCheckSetting: (settings: SettingsOnMember) => {
        set(state => {
          const tempSettings = state.memberCheckSettings?.filter(
            setting => setting.element_guid !== settings.element_guid,
          )
          if (!tempSettings) return state

          const newSettings = [...tempSettings, settings]
          return { ...state, memberCheckSettings: newSettings }
        })
      },

      setDesignForces: (designForces: CombinedDesignSupportForcesOnPosition[] | null) =>
        set({ designForces }),

      setCharacteristicMemberSupportReactions: (
        characteristicMemberSupportReactions: CharacteristicMemberSupportReactions[] | null,
      ) => set({ characteristicMemberSupportReactions }),

      changeResultElementVisibility: (element: VisibilityElements, visibility: boolean) =>
        set(state => ({
          resultElementVisibility: {
            ...state.resultElementVisibility,
            [element]: visibility,
          },
        })),

      setCornerConnectionAdditionalLoadProposal: (
        cornerConnectionAdditionalLoadProposal: CornerConnectionAdditionalLoadProposalStates,
      ) => set({ cornerConnectionAdditionalLoadProposal }),

      setSelectedProposalAnchor: (selectedProposalAnchor: string | null) =>
        set({ selectedProposalAnchor }),

      setElementListGroupingDirty: (elementListGroupingDirty: boolean) =>
        set({ elementListGroupingDirty }),

      setCommentSuggestion: (commentSuggestion: string) => set({ commentSuggestion }),
      setExtendedCommentSuggestion: (extendedCommentSuggestion: string) =>
        set({ extendedCommentSuggestion }),
      setAnchorCommentSuggestion: (anchorCommentSuggestion: string) =>
        set({ anchorCommentSuggestion }),
      setNoteSuggestion: (noteSuggestion: string) => set({ noteSuggestion }),
      setHighlightedCheck: (highlightedCheck: HighlightedCheck) => set({ highlightedCheck }),
    })),
  )

const useResultsStore = createStore()

const ResultsStoreProvider = ({ children }: Props): ReactElement => {
  const clear = useResultsStore(state => state.clear)

  useEffect(() => {
    return () => clear()
  }, [])

  return <>{children}</>
}

export { ResultsStoreProvider, useResultsStore }
